import { Injectable, inject } from '@angular/core';
import { ValidationContextService, SHEET_LABEL_FALLBACK } from './validation-context.service';
import { CrossReferenceService } from './cross-reference.service';
import { extractTaxonomyFromSchema } from './validation-taxonomy.util';
import { comparisonDifference, MBRS_EQUALITY_TOLERANCE, diffMeetsTolerance } from './validation-field-key.util';
import { SHEET_KEYS } from '../../models/sheet-keys';
import { EqualityValidationError } from './equality-validation.service';
import { CrossSheetCalcDebugService } from './cross-sheet-calc-debug.service';
import { parseFlexibleDate } from '../../utils/helpers';
import {
  ConsecutiveYearEquityValidation,
  ConsecutiveYearEquityTotalValidation,
  CrossSheetEquityByYearValidation,
} from '../../models/validation-rules.model';

/**
 * EquityReconciliationService
 *
 * Owns the consecutive-year and cross-sheet equity reconciliation validations.
 * Relocated verbatim from the legacy combined validation-warning service as
 * part of the validation-warning split; helper calls are rewired to the pure utils and
 * ValidationContextService (sheet-key resolution), while CrossReferenceService
 * is injected directly as it was in the original service.
 */
@Injectable({
  providedIn: 'root'
})
export class EquityReconciliationService {
  private ctx = inject(ValidationContextService);
  private crossRefService = inject(CrossReferenceService);
  private crossSheetCalcDebug = inject(CrossSheetCalcDebugService);

  /**
   * Validate consecutive year equity
   * Checks if ending equity from previous year matches beginning equity of current year
   * Only runs if financial years are consecutive (no gap between them)
   *
   * NOTE: `Equity:End` is a calculated field (sum of the rows above it within the
   * same equity column). Callers MUST run the row-total calculation pass and
   * write the latest values back to the form/sheet snapshot BEFORE invoking
   * this method. Otherwise this validator will compare a stale `Equity:End`
   * against `Equity:Beginning` and either miss real mismatches or surface
   * spurious ones.
   *
   * @param schema - Form schema
   * @param formValues - All form values (including all sheets); the caller is
   *                     responsible for ensuring the calculated `Equity:End`
   *                     cells inside this snapshot reflect the latest
   *                     computed roll-up.
   * @param currentTaxonomy - Current taxonomy (MFRS or MPERS)
   * @param currentSheetKey - Current sheet key
   * @returns Map of field qname to validation errors
   */
  validateConsecutiveYearEquity(
    schema: any,
    formValues: any,
    currentTaxonomy: 'MFRS' | 'MPERS',
    currentSheetKey?: string
  ): { [qname: string]: EqualityValidationError[] } {
    const errors: { [qname: string]: EqualityValidationError[] } = {};

    if (!schema || !schema.controls) {
      return errors;
    }

    // Extract schema's taxonomy
    const schemaTaxonomy = extractTaxonomyFromSchema(schema);

    // Skip if schema taxonomy doesn't match current taxonomy
    if (!schemaTaxonomy || schemaTaxonomy !== currentTaxonomy) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log(`⏭️ Schema taxonomy (${schemaTaxonomy}) doesn't match current taxonomy (${currentTaxonomy}) - skipping consecutive year equity validation`);
      return errors;
    }

    // STEP 1: Check if years are consecutive
    // Get dates from DEI (Role-1: documentAndEntityInformation)
    // DEI data lives under 'filingPrerequisites' in Phase 1 (Role-0) and
    // under 'filingInfo' in Phase 2 (Role-1). Merge both so this validation
    // runs in either phase. The date-presence check below catches the empty case.
    const deiData: Record<string, any> = {
      ...(formValues[SHEET_KEYS.filingPrerequisites] ?? {}),
      ...(formValues[SHEET_KEYS.filingInfo] ?? {}),
    };
    if (!deiData) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('⏭️ No DEI data found - skipping consecutive year equity validation');
      return errors;
    }

    // Get date fields based on taxonomy
    const currentYearStartField = 'ssmt-dei:CompanyCurrentFinancialYearStartDate';
    const previousYearEndField = 'ssmt-dei:CompanyPreviousFinancialYearEndDate';

    const currentYearStartDateStr = deiData[currentYearStartField];
    const previousYearEndDateStr = deiData[previousYearEndField];

    if (!currentYearStartDateStr || !previousYearEndDateStr) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('⏭️ Date fields not found in DEI - skipping consecutive year equity validation');
      return errors;
    }

    // Parse dates via parseFlexibleDate — DEI dates are DD/MM/YYYY (e.g.
    // "31/12/2023"); native new Date() misreads slash-dates as US MM/DD/YYYY and
    // returns Invalid Date, which silently defeated the consecutiveness check.
    const currentYearStartDate = parseFlexibleDate(currentYearStartDateStr);
    const previousYearEndDate = parseFlexibleDate(previousYearEndDateStr);

    // Check if dates are valid
    if (!currentYearStartDate || !previousYearEndDate) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('⏭️ Invalid date format - skipping consecutive year equity validation');
      return errors;
    }

    // Calculate the day after previous year end date
    const dayAfterPreviousYearEnd = new Date(previousYearEndDate);
    dayAfterPreviousYearEnd.setDate(dayAfterPreviousYearEnd.getDate() + 1);

    // Compare dates (check if current year start is the day after previous year end)
    const isConsecutive = currentYearStartDate.getTime() === dayAfterPreviousYearEnd.getTime();

    if (!isConsecutive) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log(`⏭️ Years are not consecutive - skipping validation`, {
      //   previousYearEnd: previousYearEndDateStr,
      //   currentYearStart: currentYearStartDateStr,
      //   dayAfterPreviousYearEnd: dayAfterPreviousYearEnd.toISOString().split('T')[0]
      // });
      return errors;
    }

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`✅ Years are consecutive - running consecutive year equity validation`);

    // STEP 2: Iterate through controls and find consecutiveYearEquity validations
    schema.controls.forEach((control: any) => {
      // Skip if no consecutiveYearEquity validation
      if (!control.validation?.consecutiveYearEquity) {
        return;
      }

      const rule: ConsecutiveYearEquityValidation = control.validation.consecutiveYearEquity;

      // Check rule-level taxonomy filter
      if (rule.taxonomy && rule.taxonomy.toUpperCase() !== currentTaxonomy.toUpperCase()) {
        return;
      }

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log(`🔍 Consecutive year equity validation: ${control.qname} vs ${rule.compareField}`);

      // STEP 3: Column-by-column comparison
      // Statement of Changes in Equity has multiple equity columns
      // Need to compare each column's end value (previous year) with beginning value (current year)

      // Get the sheet data
      const sheetData = currentSheetKey ? formValues[currentSheetKey] : formValues[this.ctx.resolveSheetKey(rule.sheet) ?? rule.sheet];

      if (!sheetData) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`⏭️ Sheet data not found for ${rule.sheet || currentSheetKey}`);
        return;
      }

      // Get all equity columns from schema
      const equityColumns = schema.equityColumns || [];

      if (equityColumns.length === 0) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log('⏭️ No equity columns found in schema');
        return;
      }

      const tolerance = rule.tolerance ?? MBRS_EQUALITY_TOLERANCE;

      // Iterate over consolidation types (Consolidated/Separate)
      const isConsolidated = this.crossRefService.isConsolidated();
      const consolidationTypes = isConsolidated ? ['Consolidated', 'Separate'] : ['Separate'];

      // Get labels for both fields
      const endControl = schema.controls?.find((control: any) => control.qname === rule.compareField);
      const endFieldLabel = endControl?.label || rule.compareField;
      const beginFieldLabel = control.label || control.qname;
      const sheetLabel = SHEET_LABEL_FALLBACK[SHEET_KEYS.statementOfChangesInEquity] || 'Statement of Changes in Equity';

      consolidationTypes.forEach(consolidationType => {
        // Compare each equity column (excluding total column)
        equityColumns.forEach((column: any) => {
          const columnQname = column.qname;

          // Skip total column (handled by validateConsecutiveYearEquityTotal)
          if (columnQname === 'ifrs-full:EquityMember' || columnQname === 'ifrs-smes:EquityMember') {
            return;
          }

          // Bracket notation: {rowQname}:{columnQname}[ConsolidationType][Period]
          const beginningKey = `${control.qname}:${columnQname}[${consolidationType}][CurrentYear]`;
          const endKey = `${rule.compareField}:${columnQname}[${consolidationType}][PreviousYear]`;

          const beginningValue = sheetData[beginningKey];
          const endValue = sheetData[endKey];

          // Parse to numbers
          const beginNum = (beginningValue !== null && beginningValue !== undefined && beginningValue !== '')
            ? Number(beginningValue) : null;
          const endNum = (endValue !== null && endValue !== undefined && endValue !== '')
            ? Number(endValue) : null;

          // Skip only when BOTH sides are null/empty. If one side has a value
          // and the other is empty, treat the empty side as 0 so the mismatch
          // surfaces (a missing roll-forward on one period is a real error).
          if ((beginNum === null || isNaN(beginNum as number)) && (endNum === null || isNaN(endNum as number))) {
            return;
          }
          const beginForCompare = (beginNum === null || isNaN(beginNum as number)) ? 0 : beginNum;
          const endForCompare = (endNum === null || isNaN(endNum as number)) ? 0 : endNum;

          const difference = comparisonDifference(beginForCompare, endForCompare);
          if (diffMeetsTolerance(difference, tolerance)) {
            // Error on Equity:Beginning row (Current Year table)
            // Table identifier prefix is needed for filtering (stripped on display by getRowErrorDescription)
            const columnLabel = column.label || columnQname;
            const beginMsg = `${consolidationType} Current: ${columnLabel}: Mismatch in value provided for the same field. The value should match ${endFieldLabel} (${consolidationType} Previous Year) in ${sheetLabel}.`;

            if (!errors[control.qname]) {
              errors[control.qname] = [];
            }
            errors[control.qname].push({
              severity: 'ERROR',
              message: beginMsg,
              type: 'equality',
              compareWith: { sheet: rule.sheet, field: rule.compareField },
              difference: difference,
              tolerance: tolerance
            });

            // Error on Equity:End row (Previous Year table)
            const endMsg = `${consolidationType} Previous: ${columnLabel}: Mismatch in value provided for the same field. The value should match ${beginFieldLabel} (${consolidationType} Current Year) in ${sheetLabel}.`;

            if (!errors[rule.compareField]) {
              errors[rule.compareField] = [];
            }
            errors[rule.compareField].push({
              severity: 'ERROR',
              message: endMsg,
              type: 'equality',
              compareWith: { sheet: rule.sheet, field: control.qname },
              difference: difference,
              tolerance: tolerance
            });
          }
        });
      }); // end consolidationTypes.forEach
    });

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`✅ Consecutive year equity validation completed:`, {
    //   errorsFound: Object.keys(errors).length,
    //   errors: errors
    // });

    return errors;
  }

  /**
   * Validate consecutive year equity total column
   * Checks if ending TOTAL equity from previous year matches beginning TOTAL equity of current year
   * Only runs if financial years are consecutive (no gap between them)
   * This validates ONLY the total column
   *
   * NOTE: `Equity:End` is a calculated field (sum of the rows above it within
   * the Total equity column). Callers MUST run the row-total calculation pass
   * and write the latest values back to the form/sheet snapshot BEFORE
   * invoking this method. Otherwise this validator will compare a stale
   * `Equity:End` Total against `Equity:Beginning` Total and either miss real
   * mismatches or surface spurious ones.
   *
   * @param schema - Form schema
   * @param formValues - All form values (including all sheets); the caller is
   *                     responsible for ensuring the calculated `Equity:End`
   *                     Total cells inside this snapshot reflect the latest
   *                     computed roll-up.
   * @param currentTaxonomy - Current taxonomy (MFRS or MPERS)
   * @param currentSheetKey - Current sheet key
   * @returns Map of field qname to validation errors
   */
  validateConsecutiveYearEquityTotal(
    schema: any,
    formValues: any,
    currentTaxonomy: 'MFRS' | 'MPERS',
    currentSheetKey?: string
  ): { [qname: string]: EqualityValidationError[] } {
    const errors: { [qname: string]: EqualityValidationError[] } = {};

    if (!schema || !schema.controls) {
      return errors;
    }

    // Extract schema's taxonomy
    const schemaTaxonomy = extractTaxonomyFromSchema(schema);

    // Skip if schema taxonomy doesn't match current taxonomy
    if (!schemaTaxonomy || schemaTaxonomy !== currentTaxonomy) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log(`⏭️ Schema taxonomy (${schemaTaxonomy}) doesn't match current taxonomy (${currentTaxonomy}) - skipping consecutive year equity total validation`);
      return errors;
    }

    // STEP 1: Check if years are consecutive
    // Get dates from DEI (Role-1: documentAndEntityInformation)
    // DEI data lives under 'filingPrerequisites' in Phase 1 (Role-0) and
    // under 'filingInfo' in Phase 2 (Role-1). Merge both so this validation
    // runs in either phase. The date-presence check below catches the empty case.
    const deiData: Record<string, any> = {
      ...(formValues[SHEET_KEYS.filingPrerequisites] ?? {}),
      ...(formValues[SHEET_KEYS.filingInfo] ?? {}),
    };
    if (!deiData) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('⏭️ No DEI data found - skipping consecutive year equity total validation');
      return errors;
    }

    // Get date fields based on taxonomy
    const currentYearStartField = 'ssmt-dei:CompanyCurrentFinancialYearStartDate';
    const previousYearEndField = 'ssmt-dei:CompanyPreviousFinancialYearEndDate';

    const currentYearStartDateStr = deiData[currentYearStartField];
    const previousYearEndDateStr = deiData[previousYearEndField];

    if (!currentYearStartDateStr || !previousYearEndDateStr) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('⏭️ Date fields not found in DEI - skipping consecutive year equity total validation');
      return errors;
    }

    // Parse dates via parseFlexibleDate — DEI dates are DD/MM/YYYY (e.g.
    // "31/12/2023"); native new Date() misreads slash-dates as US MM/DD/YYYY and
    // returns Invalid Date, which silently defeated the consecutiveness check.
    const currentYearStartDate = parseFlexibleDate(currentYearStartDateStr);
    const previousYearEndDate = parseFlexibleDate(previousYearEndDateStr);

    // Check if dates are valid
    if (!currentYearStartDate || !previousYearEndDate) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('⏭️ Invalid date format - skipping consecutive year equity total validation');
      return errors;
    }

    // Calculate the day after previous year end date
    const dayAfterPreviousYearEnd = new Date(previousYearEndDate);
    dayAfterPreviousYearEnd.setDate(dayAfterPreviousYearEnd.getDate() + 1);

    // Compare dates (check if current year start is the day after previous year end)
    const isConsecutive = currentYearStartDate.getTime() === dayAfterPreviousYearEnd.getTime();

    if (!isConsecutive) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log(`⏭️ Years are not consecutive - skipping total validation`, {
      //   previousYearEnd: previousYearEndDateStr,
      //   currentYearStart: currentYearStartDateStr,
      //   dayAfterPreviousYearEnd: dayAfterPreviousYearEnd.toISOString().split('T')[0]
      // });
      return errors;
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`✅ Years are consecutive - running consecutive year equity total validation`);

    // STEP 2: Iterate through controls and find consecutiveYearEquityTotal validations
    schema.controls.forEach((control: any) => {
      // Skip if no consecutiveYearEquityTotal validation
      if (!control.validation?.consecutiveYearEquityTotal) {
        return;
      }

      const rule: ConsecutiveYearEquityTotalValidation = control.validation.consecutiveYearEquityTotal;

      // Check rule-level taxonomy filter
      if (rule.taxonomy && rule.taxonomy.toUpperCase() !== currentTaxonomy.toUpperCase()) {
        return;
      }

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log(`🔍 Consecutive year equity total validation: ${control.qname} vs ${rule.compareField}`);

      // STEP 3: Compare ONLY the total column
      // Get the sheet data
      const sheetData = currentSheetKey ? formValues[currentSheetKey] : formValues[this.ctx.resolveSheetKey(rule.sheet) ?? rule.sheet];

      if (!sheetData) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`⏭️ Sheet data not found for ${rule.sheet || currentSheetKey}`);
        return;
      }

      // Determine the total column qname based on taxonomy
      const totalColumnQname = currentTaxonomy === 'MFRS'
        ? 'ifrs-full:EquityMember'
        : 'ifrs-smes:EquityMember';

      const tolerance = rule.tolerance ?? MBRS_EQUALITY_TOLERANCE;

      // Iterate over consolidation types (Consolidated/Separate)
      const isConsolidated = this.crossRefService.isConsolidated();
      const consolidationTypes = isConsolidated ? ['Consolidated', 'Separate'] : ['Separate'];

      consolidationTypes.forEach(consolidationType => {
        // Bracket notation: {rowQname}:{columnQname}[ConsolidationType][Period]
        const beginningKey = `${control.qname}:${totalColumnQname}[${consolidationType}][CurrentYear]`;
        const endKey = `${rule.compareField}:${totalColumnQname}[${consolidationType}][PreviousYear]`;

        const beginningRaw = sheetData[beginningKey];
        const endRaw = sheetData[endKey];

        const beginNum = (beginningRaw !== null && beginningRaw !== undefined && beginningRaw !== '')
          ? Number(beginningRaw) : null;
        const endNum = (endRaw !== null && endRaw !== undefined && endRaw !== '')
          ? Number(endRaw) : null;

        // Skip only when BOTH sides are null/empty. If one side has a value
        // and the other is empty, treat the empty side as 0 so the mismatch
        // surfaces (a missing roll-forward on one period is a real error).
        if ((beginNum === null || isNaN(beginNum as number)) && (endNum === null || isNaN(endNum as number))) {
          return;
        }
        const beginForCompare = (beginNum === null || isNaN(beginNum as number)) ? 0 : beginNum;
        const endForCompare = (endNum === null || isNaN(endNum as number)) ? 0 : endNum;

        const difference = comparisonDifference(beginForCompare, endForCompare);

        if (diffMeetsTolerance(difference, tolerance)) {
          const endCtrl = schema.controls?.find((control: any) => control.qname === rule.compareField);
          const endLabel = endCtrl?.label || rule.compareField;
          const sheetLbl = SHEET_LABEL_FALLBACK[SHEET_KEYS.statementOfChangesInEquity] || 'Statement of Changes in Equity';
          const beginLabel = control.label || control.qname;

          // Total column label
          const totalLabel = 'Total';

          // Error on Equity:Beginning row (Current Year table)
          // Table identifier prefix is needed for filtering (stripped on display by getRowErrorDescription)
          const beginMsg = rule.message ??
            `${consolidationType} Current: ${totalLabel}: Mismatch in value provided for the same field. The value should match ${endLabel} (${consolidationType} Previous Year) in ${sheetLbl}.`;

          if (!errors[control.qname]) {
            errors[control.qname] = [];
          }
          errors[control.qname].push({
            severity: 'ERROR',
            message: beginMsg,
            type: 'equality',
            compareWith: { sheet: rule.sheet, field: rule.compareField },
            difference: difference,
            tolerance: tolerance
          });

          // Error on Equity:End row (Previous Year table)
          const endMsg = `${consolidationType} Previous: ${totalLabel}: Mismatch in value provided for the same field. The value should match ${beginLabel} (${consolidationType} Current Year) in ${sheetLbl}.`;

          if (!errors[rule.compareField]) {
            errors[rule.compareField] = [];
          }
          errors[rule.compareField].push({
            severity: 'ERROR',
            message: endMsg,
            type: 'equality',
            compareWith: { sheet: rule.sheet, field: control.qname },
            difference: difference,
            tolerance: tolerance
          });
        }
      });
    });

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`✅ Consecutive year equity total validation completed:`, {
    //   errorsFound: Object.keys(errors).length,
    //   errors: errors
    // });

    return errors;
  }

  /**
   * Validate cross-sheet equity by year
   * Compares equity values between Statement of Financial Position and Statement of Changes in Equity
   * Validates separately for Current and Previous years
   * Shows errors on BOTH fields if validation fails
   *
   * @param schemas - Object containing both schemas (SFP and SOCIE)
   * @param formValues - All form values from all sheets
   * @param currentTaxonomy - Current taxonomy (MFRS or MPERS)
   * @returns Object with qname keys and error arrays
   */
  validateCrossSheetEquityByYear(
    schemas: { sfp: any; socie: any },
    formValues: any,
    currentTaxonomy: 'MFRS' | 'MPERS'
  ): { [qname: string]: EqualityValidationError[] } {
    const errors: { [qname: string]: EqualityValidationError[] } = {};

    if (!schemas.sfp || !schemas.socie || !schemas.sfp.controls || !schemas.socie.controls) {
      return errors;
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`🔍 Starting cross-sheet equity by year validation (${currentTaxonomy})`);

    // Get the field qnames based on taxonomy
    const sfpEquityQname = currentTaxonomy === 'MFRS' ? 'ifrs-full:Equity' : 'ifrs-smes:Equity';
    const socieEquityEndQname = currentTaxonomy === 'MFRS' ? 'ifrs-full:Equity:End' : 'ifrs-smes:Equity:End';
    const totalColumnQname = currentTaxonomy === 'MFRS' ? 'ifrs-full:EquityMember' : 'ifrs-smes:EquityMember';

    // Find the controls with crossSheetEquityByYear validation
    const sfpControl = schemas.sfp.controls.find((control: any) => control.qname === sfpEquityQname);
    const socieControl = schemas.socie.controls.find((control: any) => control.qname === socieEquityEndQname);

    if (!sfpControl?.validation?.crossSheetEquityByYear && !socieControl?.validation?.crossSheetEquityByYear) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('⏭️ No crossSheetEquityByYear validation found in either schema');
      return errors;
    }

    // Get the validation rule (could be defined on either control)
    const rule: CrossSheetEquityByYearValidation | undefined =
      sfpControl?.validation?.crossSheetEquityByYear ||
      socieControl?.validation?.crossSheetEquityByYear;

    if (!rule) {
      return errors;
    }

    // Check rule-level taxonomy filter
    if (rule.taxonomy && rule.taxonomy.toUpperCase() !== currentTaxonomy.toUpperCase()) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log(`⏭️ Rule taxonomy filter (${rule.taxonomy}) doesn't match current taxonomy (${currentTaxonomy})`);
      return errors;
    }

    // AS-1076 period scope: the Current-Year leg reconciles UNGATED; the
    // Previous-Year leg only on a consecutive FY boundary AND a subsequent
    // (non-first-time) filing. So the boundary is computed defensively below —
    // we never early-return on missing/invalid/non-consecutive dates, because
    // that would suppress the Current-Year comparison too.
    //
    // DEI data lives under 'filingPrerequisites' in Phase 1 (Role-0) and under
    // 'filingInfo' in Phase 2 (Role-1). Merge both so this runs in either phase.
    const deiData: Record<string, any> = {
      ...(formValues[SHEET_KEYS.filingPrerequisites] ?? {}),
      ...(formValues[SHEET_KEYS.filingInfo] ?? {}),
    };

    const currentYearStartField = 'ssmt-dei:CompanyCurrentFinancialYearStartDate';
    const previousYearEndField = 'ssmt-dei:CompanyPreviousFinancialYearEndDate';
    const currentYearStartDateStr = deiData[currentYearStartField];
    const previousYearEndDateStr = deiData[previousYearEndField];

    // Consecutive iff both dates are present, valid, and
    // current-start === previous-end + 1 day. Anything else → not consecutive
    // (the Previous-Year leg stays gated out; the Current-Year leg still runs).
    let isConsecutive = false;
    if (currentYearStartDateStr && previousYearEndDateStr) {
      // parseFlexibleDate — DEI dates are DD/MM/YYYY; native new Date() misreads
      // slash-dates as US MM/DD/YYYY → Invalid Date → false "not consecutive".
      const currentYearStartDate = parseFlexibleDate(currentYearStartDateStr);
      const previousYearEndDate = parseFlexibleDate(previousYearEndDateStr);
      if (currentYearStartDate && previousYearEndDate) {
        const dayAfterPreviousYearEnd = new Date(previousYearEndDate);
        dayAfterPreviousYearEnd.setDate(dayAfterPreviousYearEnd.getDate() + 1);
        isConsecutive =
          currentYearStartDate.getTime() === dayAfterPreviousYearEnd.getTime();
      }
    }

    // Get sheet data
    const sfpData = formValues[SHEET_KEYS.statementOfFinancialPosition];
    const socieData = formValues[SHEET_KEYS.statementOfChangesInEquity];

    if (!sfpData || !socieData) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('⏭️ Sheet data not found for one or both sheets');
      return errors;
    }

    const tolerance = rule.tolerance ?? MBRS_EQUALITY_TOLERANCE;

    // Get labels for error messages (reuse sfpControl from line above, find socieEndControl)
    const socieEndControl = socieControl;
    const sfpLabel = sfpControl?.label || sfpEquityQname;
    const socieEndLabel = socieEndControl?.label || socieEquityEndQname;
    const sfpSheetLabel = SHEET_LABEL_FALLBACK[SHEET_KEYS.statementOfFinancialPosition] || 'Statement of Financial Position';
    const socieSheetLabel = SHEET_LABEL_FALLBACK[SHEET_KEYS.statementOfChangesInEquity] || 'Statement of Changes in Equity';

    // Period scope (AS-1076): Current Year is always reconciled (Consolidated +
    // Separate); Previous Year only on a consecutive boundary AND a subsequent
    // filing. (For a first-time filing no Previous-Year column is rendered
    // anyway; the isSubsequent gate keeps the editor and headless verdicts
    // aligned for that case.)
    const isConsolidated = this.crossRefService.isConsolidated();
    const isSubsequent = this.crossRefService.isSubsequentPreparation();
    const consolidationTypes = isConsolidated ? ['Consolidated', 'Separate'] : ['Separate'];
    const periodTypes: string[] = [
      'CurrentYear',
      ...(isConsecutive && isSubsequent ? ['PreviousYear'] : []),
    ];

    consolidationTypes.forEach(consolidationType => {
      periodTypes.forEach(period => {
        // SFP: dualColumn → {qname}[ConsolidationType][Period]
        const sfpKey = `${sfpEquityQname}[${consolidationType}][${period}]`;
        // SOCIE: equityColumns → {qname}:{totalColumn}[ConsolidationType][Period]
        const socieKey = `${socieEquityEndQname}:${totalColumnQname}[${consolidationType}][${period}]`;

        const sfpRaw = sfpData[sfpKey];
        const socieRaw = socieData[socieKey];

        // For calculated/readonly fields, treat null/undefined as 0 (they display as 0)
        const sfpIsCalculated = sfpControl?.ui?.calculated || sfpControl?.ui?.readonly;
        const socieIsCalculated = socieEndControl?.ui?.calculated || socieEndControl?.ui?.readonly;

        let sfpNum: number | null;
        if (sfpRaw !== null && sfpRaw !== undefined && sfpRaw !== '') {
          sfpNum = Number(sfpRaw);
        } else {
          sfpNum = sfpIsCalculated ? 0 : null;
        }

        let socieNum: number | null;
        if (socieRaw !== null && socieRaw !== undefined && socieRaw !== '') {
          socieNum = Number(socieRaw);
        } else {
          socieNum = socieIsCalculated ? 0 : null;
        }

        // Skip if either is null or NaN
        if (sfpNum === null || socieNum === null || isNaN(sfpNum) || isNaN(socieNum)) {
          return;
        }

        // Round both operands to the 2-d.p. MBRS display precision before
        // diffing (shared policy — see comparisonDifference). This stops a
        // sub-cent gap between a recomputed 2-d.p. value and the un-rounded
        // N8N-extracted figure behind it from raising a spurious mismatch.
        const difference = comparisonDifference(sfpNum, socieNum);

        // Diagnostic only (no-op unless RuntimeConfig.crossSheetCalcDebug). The
        // compared SOCIE value is read from the persisted snapshot, so a flag
        // that survives rounding points at a stale/un-recomputed operand.
        this.crossSheetCalcDebug.log({
          source: 'equity-reconciliation:byYear',
          currentSheet: SHEET_KEYS.statementOfFinancialPosition,
          currentField: sfpEquityQname,
          compareSheet: SHEET_KEYS.statementOfChangesInEquity,
          compareField: socieEquityEndQname,
          column: `${consolidationType} ${period}`,
          currentRaw: sfpRaw,
          compareRaw: socieRaw,
          tolerance,
          rounds: true,
        });

        if (diffMeetsTolerance(difference, tolerance)) {
          const periodLabel = period === 'CurrentYear' ? 'Current Year' : 'Previous Year';
          const columnLabel = `${consolidationType} ${period === 'CurrentYear' ? 'Current' : 'Previous'}`;

          // Error on SOCIE Equity:End row — use "Total:" prefix (table context handled by SOCIE template)
          const socieMsg = `${columnLabel}: Total: Mismatch in value provided for the same field. The value should match ${sfpLabel} (${consolidationType} ${periodLabel}) in ${sfpSheetLabel}.`;

          if (!errors[socieEquityEndQname]) {
            errors[socieEquityEndQname] = [];
          }
          errors[socieEquityEndQname].push({
            severity: 'ERROR',
            message: socieMsg,
            type: 'equality',
            compareWith: { sheet: SHEET_KEYS.statementOfFinancialPosition, field: sfpEquityQname },
            difference: difference,
            tolerance: tolerance
          });

          // Error on SFP Equity row — include consolidation + period in the field reference
          // so the message identifies which SOCIE column the value should reconcile to
          // (mirrors the SOCIE-side format at socieMsg above).
          const sfpMsg = `${columnLabel}: Mismatch in value provided for the same field. The value should match ${socieEndLabel} (${consolidationType} ${periodLabel}) in ${socieSheetLabel}.`;

          if (!errors[sfpEquityQname]) {
            errors[sfpEquityQname] = [];
          }
          errors[sfpEquityQname].push({
            severity: 'ERROR',
            message: sfpMsg,
            type: 'equality',
            compareWith: { sheet: SHEET_KEYS.statementOfChangesInEquity, field: socieEquityEndQname },
            difference: difference,
            tolerance: tolerance
          });
        }
      });
    });

    return errors;
  }
}
